import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import { showToast } from "components/ui/toast";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Edit,
  Eye,
  FileText,
  GripVertical,
  LayoutGrid,
  List,
  Paperclip,
  Plus,
  Presentation,
  Save,
  Search,
  Trash2,
  Upload,
  Video,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "../utils/axios";
import { formatDateToAEDT } from "utils/helperFunctions";

// Main Component
export default function TrainMeLibrary() {
  const [activeTab, setActiveTab] = useState("modules");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [modules, setModules] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingFile, setEditingFile] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [isCreatingModule, setIsCreatingModule] = useState(false);
  const [newModule, setNewModule] = useState({ title: "", description: "" });
  const [activeFileView, setActiveFileView] = useState("grid");
  const [showFileDetails, setShowFileDetails] = useState(null);
  const [showFileModal, setShowFileModal] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [modalSelectedFiles, setModalSelectedFiles] = useState(new Set());
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [priceProcess, setPriceProcess] = useState([]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFiles();
    fetchModules();
    fetchMeetings();
    fetchPriceProcess();
  }, []);

  const fetchFiles = async () => {
    try {
      const res = await axios.get("/admin/trainme?type=file");
      setUploadedFiles(res.data.data);
    } catch (err) {
      console.error("Failed to fetch files:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await axios.get("/admin/trainme?type=module");
      setModules(res.data.data);
    } catch (err) {
      console.error("Failed to fetch files:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeetings = async () => {
    try {
      const res = await axios.get("/admin/trainme?type=meeting");
      setMeetingTypes(res.data.data);
    } catch (err) {
      console.error("Failed to fetch files:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceProcess = async () => {
    try {
      const res = await axios.get("/admin/trainme?type=priceProcess");
      setPriceProcess(res.data.data);
    } catch (err) {
      console.error("Failed to fetch files:", err);
    } finally {
      setLoading(false);
    }
  };

  // Utility functions
  const getFileIcon = (type) => {
    if (type?.includes("video")) return <Video className="h-5 w-5" />;
    if (type?.includes("pdf")) return <FileText className="h-5 w-5" />;
    if (type?.includes("slideshow"))
      return <Presentation className="h-5 w-5" />;
    return <FileText className="h-5 w-5" />;
  };

  const getFileTypeColor = (type) => {
    if (type?.includes("video")) return "bg-red-50 text-red-700 border-red-200";
    if (type?.includes("pdf"))
      return "bg-blue-50 text-blue-700 border-blue-200";
    if (type?.includes("image"))
      return "bg-green-50 text-green-700 border-green-200";
    if (type?.includes("presentation"))
      return "bg-purple-50 text-purple-700 border-purple-200";
    return "bg-lightergray text-darkergray border-mediumgray";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  // File handling functions
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const getDurationFromFile = (file) => {
    return new Promise((resolve) => {
      const media = document.createElement(
        file.type.startsWith("video") ? "video" : "audio"
      );
      media.preload = "metadata";
      media.src = URL.createObjectURL(file);
      media.onloadedmetadata = () => {
        URL.revokeObjectURL(media.src);
        resolve(media.duration);
      };
    });
  };

  const uploadFileToServer = async (file) => {
    let duration = null;
    const fileType = file.type;

    if (fileType.startsWith("video") || fileType.startsWith("audio")) {
      duration = await getDurationFromFile(file);
    }

    const formData = new FormData();
    formData.append("file", file);
    if (duration) formData.append("duration", Math.ceil(duration)); // in seconds
    setLoading(true);
    try {
      const response = await axios.post(
        "/admin/trainme/upload/file",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setLoading(false);
      return response.data; // your uploaded file URL or meta from backend
    } catch (err) {
      console.error("Upload failed", err);
      setLoading(false);
      return null;
    }
  };
  const handleFiles = async (files) => {
    const newFiles = await Promise.all(
      Array.from(files).map(async (file, index) => {
        const uploadResult = await uploadFileToServer(file);

        return {
          _id: uploadResult?.file?._id,
          title: file.name,
          description: "",
          type: uploadResult?.file.fileType,
          size: uploadResult?.file.fileSize,
          url: uploadResult?.file.s3Url || URL.createObjectURL(file), // fallback to preview URL
          uploadDate: uploadResult?.file?.createdAt,
          order: uploadedFiles.length + index + 1,
        };
      })
    );

    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleFileInput = (e) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = async (fileId) => {
    const response = await axios.delete(`/admin/trainme/file/delete/${fileId}`);
    if (response.data.success) {
      showToast("success", response.data.message);
    }

    setUploadedFiles((prev) => prev.filter((file) => file._id !== fileId));

    // Remove from modules
    setModules((prev) =>
      prev.map((module) => ({
        ...module,
        files: module.files.filter((file) => file._id !== fileId),
      }))
    );

    // Remove from meeting attachments
    setMeetingTypes((prev) =>
      prev.map((meeting) => ({
        ...meeting,
        files: meeting.files.filter((file) => file._id !== fileId),
      }))
    );
  };

  const toggleModalFileSelection = (fileId) => {
    setModalSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  const updateFile = (fileId, updates) => {
    setUploadedFiles((prev) =>
      prev.map((file) => (file._id === fileId ? { ...file, ...updates } : file))
    );

    // Update in modules
    setModules((prev) =>
      prev.map((module) => ({
        ...module,
        files: module.files.map((file) =>
          file._id === fileId ? { ...file, ...updates } : file
        ),
      }))
    );

    // Update in meeting attachments
    setMeetingTypes((prev) =>
      prev.map((meeting) => ({
        ...meeting,
        files: meeting.files.map((file) =>
          file._id === fileId ? { ...file, ...updates } : file
        ),
      }))
    );
  };

  // Preview functions
  const openPreview = (file) => {
    setPreviewFile(file);
    setShowPreviewModal(true);
  };

  const closePreview = () => {
    setPreviewFile(null);
    setShowPreviewModal(false);
  };

  // Module functions
  const addModule = async () => {
    if (!newModule.title.trim()) return;

    const newModuleObj = {
      title: newModule.title,
      description: newModule.description,
      order: modules.length + 1,
    };

    const response = await axios.post("/admin/trainme/module", newModuleObj);
    if (response.data.success) {
      setModules((prev) => [...prev, response.data.data]);

      showToast("success", response.data.message);
    }

    setNewModule({ title: "", description: "" });
    setIsCreatingModule(false);
  };

  function debounce(func, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  // ✅ Define once
  const debouncedModuleApiCall = useMemo(
    () =>
      debounce(async (moduleId, updateModuleObj, updates) => {
        const response = await axios.put(
          "/admin/trainme?type=module",
          updateModuleObj
        );
        if (response.data.success) {
          if ("files" in updates || "filesToRemove" in updates) {
            setModules((prev) =>
              prev.map((module) =>
                module._id === moduleId ? { ...response.data.data } : module
              )
            );
          }
          showToast("success", response.data.message);
        }
      }, 500),
    []
  );

  const updateModule = async (moduleId, updates) => {
    const updateModuleObj = {
      ...updates,
      moduleId,
    };

    if (!("files" in updates) && !("filesToRemove" in updates)) {
      setModules((prev) =>
        prev.map((module) =>
          module._id === moduleId ? { ...module, ...updates } : module
        )
      );
    }
    // Call debounced function
    debouncedModuleApiCall(moduleId, updateModuleObj, updates);
  };

  const meetingApiCall = async (meetingId, updateMeetingObj, updates) => {
    try {
      const response = await axios.put(
        "/admin/trainme?type=meeting",
        updateMeetingObj
      );

      if (response.data.success) {
        if ("files" in updates || "filesToRemove" in updates) {
          setMeetingTypes((prev) =>
            prev.map((meeting) =>
              meeting._id === meetingId ? { ...response.data.data } : meeting
            )
          );
        }
        showToast("success", response.data.message);
      }
    } catch (error) {
      console.error("error: ", error);
      showToast("error", "Something went wrong");
    }
  };

  const updateMeeting = async (meetingId, updates) => {
    const updateMeetingObj = {
      ...updates,
      meetingId,
    };

    // Call API
    meetingApiCall(meetingId, updateMeetingObj, updates);
  };

  const removeModule = async (moduleId) => {
    const response = await axios.delete(`/admin/trainme/module/${moduleId}`);
    if (response.data.success) {
      showToast("success", response.data.message);
    }

    setModules((prev) => prev.filter((module) => module._id !== moduleId));
  };

  // Drag and drop reordering for modules
  const handleModuleDragStart = (moduleId) => {
    setDraggedItem({ type: "module", _id: moduleId });
  };

  const handleModuleDragOver = (moduleId) => {
    if (draggedItem?.type === "module" && draggedItem._id !== moduleId) {
      setDragOverItem({ type: "module", _id: moduleId });
    }
  };

  const handleModuleDrop = () => {
    if (draggedItem?.type === "module" && dragOverItem?.type === "module") {
      const draggedModuleIndex = modules.findIndex(
        (m) => m._id === draggedItem._id
      );
      const dropModuleIndex = modules.findIndex(
        (m) => m._id === dragOverItem._id
      );

      if (draggedModuleIndex !== -1 && dropModuleIndex !== -1) {
        const newModules = [...modules];
        const [movedModule] = newModules.splice(draggedModuleIndex, 1);
        newModules.splice(dropModuleIndex, 0, movedModule);

        // Update order
        const updatedModules = newModules.map((module, index) => ({
          ...module,
          order: index + 1,
        }));

        setModules(updatedModules);
      }
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Drag and drop reordering for files within modules
  const handleFileDragStart = (moduleId, fileId) => {
    setDraggedItem({ type: "file", moduleId, fileId });
  };

  const handleFileDragOver = (moduleId, fileId) => {
    if (
      draggedItem?.type === "file" &&
      draggedItem.moduleId === moduleId &&
      draggedItem.fileId !== fileId
    ) {
      setDragOverItem({ type: "file", moduleId, fileId });
    }
  };

  const handleFileDrop = () => {
    if (
      draggedItem?.type === "file" &&
      dragOverItem?.type === "file" &&
      draggedItem.moduleId === dragOverItem.moduleId
    ) {
      const moduleIndex = modules.findIndex(
        (m) => m._id === draggedItem.moduleId
      );

      if (moduleIndex !== -1) {
        const module = modules[moduleIndex];
        const draggedFileIndex = module.files.findIndex(
          (f) => f._id === draggedItem.fileId
        );
        const dropFileIndex = module.files.findIndex(
          (f) => f._id === dragOverItem.fileId
        );

        if (draggedFileIndex !== -1 && dropFileIndex !== -1) {
          const newFiles = [...module.files];
          const [movedFile] = newFiles.splice(draggedFileIndex, 1);
          newFiles.splice(dropFileIndex, 0, movedFile);

          // Update order
          const updatedFiles = newFiles.map((file, index) => ({
            ...file,
            order: index + 1,
          }));

          const updatedModules = [...modules];
          updatedModules[moduleIndex] = {
            ...module,
            files: updatedFiles,
          };

          setModules(updatedModules);
        }
      }
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Meeting attachment functions
  const removeFileFromMeeting = (meetingId, fileId) => {
    updateMeeting(meetingId, { filesToRemove: [fileId] });
  };

  const processApiCall = async (processId, updateProcessObj, updates) => {
    try {
      const response = await axios.put(
        "/admin/trainme?type=priceProcess",
        updateProcessObj
      );

      if (response.data.success) {
        if ("files" in updates || "filesToRemove" in updates) {
          setPriceProcess((prev) =>
            prev.map((process) =>
              process._id === processId ? { ...response.data.data } : process
            )
          );
        }
        showToast("success", response.data.message);
      }
    } catch (error) {
      console.error("error: ", error);
      showToast("error", "Something went wrong");
    }
  };

  // Price and Process attachment functions
  const removeFileFromProcess = (processId, fileId) => {
    updateProcess(processId, { filesToRemove: [fileId] });
  };

  const updateProcess = async (processId, updates) => {
    const updateProcessObj = {
      ...updates,
      processId,
    };

    // Call API
    processApiCall(processId, updateProcessObj, updates);
  };

  const handleUpdateFile = async (fileId, name, description) => {
    const updateFileObj = {
      fileId,
      name,
      description,
    };

    const response = await axios.put("/admin/trainme?type=file", updateFileObj);
    if (response.data.success) {
      showToast("success", response.data.message);
    } else {
      showToast("error", response.data.message);
    }
  };

  // Modal file management
  const openFileModal = (moduleId, meetingId = null, processId = null) => {
    setSelectedModuleId(moduleId);
    setSelectedMeetingId(meetingId);
    setSelectedProcessId(processId);
    setShowFileModal(true);
    setModalSelectedFiles(new Set());
    setModalSearchTerm("");
  };

  const closeFileModal = () => {
    setShowFileModal(false);
    setSelectedModuleId(null);
    setSelectedMeetingId(null);
    setSelectedProcessId(null);
    setModalSelectedFiles(new Set());
    setModalSearchTerm("");
  };

  const addSelectedFilesToModule = () => {
    if (!selectedModuleId || modalSelectedFiles.size === 0) return;

    const filesToAdd = uploadedFiles
      ?.filter((file) => modalSelectedFiles.has(file._id))
      .map((file) => file._id);

    updateModule(selectedModuleId, { files: filesToAdd });

    closeFileModal();
  };

  const addSelectedFilesToMeeting = () => {
    if (!selectedMeetingId || modalSelectedFiles.size === 0) return;

    const filesToAdd = uploadedFiles
      ?.filter((file) => modalSelectedFiles.has(file._id))
      .map((file) => file._id);

    updateMeeting(selectedMeetingId, { files: filesToAdd });

    closeFileModal();
  };

  const addSelectedFilesToProcess = () => {
    if (!selectedProcessId || modalSelectedFiles.size === 0) return;

    const filesToAdd = uploadedFiles
      ?.filter((file) => modalSelectedFiles.has(file._id))
      .map((file) => file._id);

    updateProcess(selectedProcessId, { files: filesToAdd });

    closeFileModal();
  };

  const removeFileFromModule = (moduleId, fileId) => {
    updateModule(moduleId, { filesToRemove: [fileId] });
  };

  // Get available files for modal (not already in the current module/meeting)
  const getAvailableFiles = () => {
    if (selectedModuleId) {
      const currentModule = modules.find((m) => m._id === selectedModuleId);
      const moduleFileIds = new Set(
        currentModule?.files.map((f) => f._id) || []
      );
      return uploadedFiles?.filter((file) => !moduleFileIds.has(file._id));
    }

    if (selectedMeetingId) {
      const currentMeeting = meetingTypes.find(
        (m) => m._id === selectedMeetingId
      );
      const meetingFileIds = new Set(
        currentMeeting?.files.map((f) => f._id) || []
      );
      return uploadedFiles?.filter((file) => !meetingFileIds.has(file._id));
    }

    if (selectedProcessId) {
      const currentPriceProcess = priceProcess.find(
        (p) => p._id === selectedProcessId
      );
      const processFileIds = new Set(
        currentPriceProcess?.files.map((f) => f._id) || []
      );
      return uploadedFiles?.filter((file) => !processFileIds.has(file._id));
    }

    return [];
  };

  // Filtered files based on search
  const filteredFiles = uploadedFiles?.filter(
    (file) =>
      file.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filtered modal files based on search
  const filteredModalFiles = getAvailableFiles().filter(
    (file) =>
      file.title.toLowerCase().includes(modalSearchTerm.toLowerCase()) ||
      file.description.toLowerCase().includes(modalSearchTerm.toLowerCase())
  );

  // Custom Badge Component
  const Badge = ({ children, variant = "default", className = "" }) => {
    const baseStyles =
      "hidden sm:inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium";

    const variants = {
      default: "bg-blue-50 text-blue-700 border border-blue-200",
      outline: "border border-mediumgray text-darkergray",
      secondary: "bg-lightgray text-darkergray",
    };

    return (
      <span className={`${baseStyles} ${variants[variant]} ${className}`}>
        {children}
      </span>
    );
  };

  // Custom Tab Component
  const Tab = ({ active, onClick, children }) => {
    return (
      <Button
        onClick={onClick}
        className={`px-4 py-2 font-medium text-sm transition-all ${
          active
            ? "text-darkergray border-b-2 border-darkgray"
            : "text-darkgray hover:text-darkergray border-b-2 border-transparent hover:border-mediumgray"
        }`}
      >
        {children}
      </Button>
    );
  };

  // Preview Modal Component
  const PreviewModal = ({ isOpen, onClose, file }) => {
    if (!isOpen || !file) return null;

    const renderPreview = () => {
      if (file.type.includes("video")) {
        return (
          <div className="w-full">
            <video
              controls
              className="w-full max-h-96 rounded-lg"
              src={file.url}
            />
          </div>
        );
      }
      if (file.type.includes("audio")) {
        return (
          <div className="w-full">
            <audio controls className="w-full rounded-lg" src={file.url} />
          </div>
        );
      }

      if (file.type.includes("pdf")) {
        return (
          <div className="w-full">
            <iframe
              src={file.url}
              className="w-full h-96 rounded-lg border"
              title={file.title}
            ></iframe>
          </div>
        );
      }

      if (file.type.includes("notes")) {
        return (
          <div className="w-full">
            <iframe
              src={`https://docs.google.com/gview?url=${file.url}&embedded=true`}
              className="w-full h-96 rounded-lg border"
              title={file.title}
            />
          </div>
        );
      }
      if (file.type.includes("slideshow")) {
        return (
          <div className="w-full">
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                file.url
              )}`}
              className="w-full h-96 rounded-lg border"
              title={file.title}
            />
          </div>
        );
      }

      // For other file types, show file info
      return (
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-lightgray mb-4">
            {getFileIcon(file.type)}
          </div>
          <h4 className="mb-2">{file.title}</h4>
          <p className="text-sm text-darkergray mb-4">
            {file.description || "No description available"}
          </p>
          <div className="space-y-2 text-sm text-darkergray">
            <p>
              <span className="font-medium">File Type:</span> {file.type}
            </p>
            <p>
              <span className="font-medium">Size:</span>{" "}
              {formatFileSize(file.size)}
            </p>
            <p>
              <span className="font-medium">Uploaded:</span>{" "}
              {formatDateToAEDT(file.uploadDate)}
            </p>
          </div>
          <Button
            className="mt-4 black-button"
            onClick={() => window.open(file.url, "_blank")}
          >
            Download File
          </Button>
        </div>
      );
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Preview: ${file.title}`}
        size="large"
      >
        <div className="space-y-4">
          {renderPreview()}
          <div className="flex justify-between items-center pt-4 border-t border-mediumgray">
            <div className="flex items-center space-x-2">
              <Badge className={getFileTypeColor(file.type)}>
                {file.type.toUpperCase() || "FILE"}
              </Badge>
              <span className="text-sm text-darkergray">
                {formatFileSize(file.size)}
              </span>
            </div>
            <Button
              variant="outline"
              className="outline-button"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <div className=" bg-lightergray">
      {/* Header */}
      <header className="bg-white border-b border-mediumgray sticky top-0 z-10">
        <div className="max-width mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between items-center py-4 sm:flex-row sm:py-0 h-full">
            <h2>Train Me Library</h2>

            <div className="flex items-center space-x-4">
              <Tab
                active={activeTab === "modules"}
                onClick={() => setActiveTab("modules")}
              >
                Modules
              </Tab>
              <Tab
                active={activeTab === "files"}
                onClick={() => setActiveTab("files")}
              >
                Files
              </Tab>
              <Tab
                active={activeTab === "meetings"}
                onClick={() => setActiveTab("meetings")}
              >
                Meetings
              </Tab>
              <Tab
                active={activeTab === "priceProcess"}
                onClick={() => setActiveTab("priceProcess")}
              >
                Price & Process
              </Tab>
            </div>
          </div>
        </div>
      </header>

      <main className="max-width mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Modules Tab */}
        {activeTab === "modules" && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <h3>Training Modules</h3>
              <Button
                className="flex black-button"
                onClick={() => setIsCreatingModule(true)}
                disabled={isCreatingModule}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Module
              </Button>
            </div>

            {/* New Module Form */}
            {isCreatingModule && (
              <div className="bg-white p-6 rounded-lg shadow-sm border border-mediumgray space-y-4">
                <h3>Create New Module</h3>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="module-title"
                      className="block text-sm font-medium text-darkergray mb-1"
                    >
                      Module Title
                    </label>
                    <input
                      id="module-title"
                      value={newModule.title}
                      onChange={(e) =>
                        setNewModule((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-mediumgray rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-mediumgray focus:border-darkgray "
                      placeholder="Enter module title"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="module-description"
                      className="block text-sm font-medium text-darkergray mb-1"
                    >
                      Description
                    </label>
                    <textarea
                      id="module-description"
                      value={newModule.description}
                      onChange={(e) =>
                        setNewModule((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-mediumgray rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-mediumgray focus:border-darkgray"
                      placeholder="Enter module description"
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button
                      className="outline-button"
                      // variant="outline"
                      onClick={() => {
                        setIsCreatingModule(false);
                        setNewModule({ title: "", description: "" });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="black-button"
                      onClick={addModule}
                      disabled={!newModule.title.trim()}
                    >
                      Create Module
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Modules List */}
            <div className="space-y-6">
              {modules.length === 0 ? (
                <div className="bg-white p-12 rounded-lg shadow-sm border border-mediumgray text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                    <Plus className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="mt-2">No modules yet</h3>
                  <p className="mt-1 text-sm text-darkergray">
                    Get started by creating a new training module.
                  </p>
                  <div className="mt-6">
                    <Button
                      className="black-button"
                      onClick={() => setIsCreatingModule(true)}
                    >
                      Add Module
                    </Button>
                  </div>
                </div>
              ) : (
                modules.map((module, index) => (
                  <div
                    key={module._id}
                    className={`bg-white rounded-lg shadow-sm border ${
                      draggedItem?.type === "module" &&
                      draggedItem._id === module._id
                        ? "border-blue-500 opacity-50"
                        : dragOverItem?.type === "module" &&
                          dragOverItem._id === module._id
                        ? "border-blue-300 bg-blue-50"
                        : "border-mediumgray"
                    } transition-all`}
                    draggable
                    onDragStart={() => handleModuleDragStart(module._id)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      handleModuleDragOver(module._id);
                    }}
                    onDragEnd={handleModuleDrop}
                  >
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className="cursor-move p-1 text-darkgray hover:text-darkergray"
                            onMouseDown={() =>
                              handleModuleDragStart(module._id)
                            }
                          >
                            <GripVertical className="h-5 w-5" />
                          </div>
                          <div className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-900 font-medium">
                            {module.order}
                          </div>
                          {editingModule === module._id ? (
                            <input
                              value={module.title}
                              onChange={(e) =>
                                updateModule(module._id, {
                                  title: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 border border-mediumgray rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-mediumgray focus:border-darkgray font-medium text-lg"
                              onBlur={() => setEditingModule(null)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") setEditingModule(null);
                              }}
                            />
                          ) : (
                            <h3
                              className=" cursor-pointer hover:text-darkergray"
                              onClick={() => setEditingModule(module._id)}
                            >
                              {module.title}
                            </h3>
                          )}
                          <Badge>{module.files.length} files</Badge>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingModule(module._id)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeModule(module._id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {editingModule === module._id ? (
                        <textarea
                          value={module.description}
                          onChange={(e) =>
                            updateModule(module._id, {
                              description: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-mediumgray rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-mediumgray focus:border-darkgray  mt-2"
                          rows={2}
                        />
                      ) : (
                        <p className="mt-2 text-sm text-darkergray">
                          {module.description}
                        </p>
                      )}

                      {/* Module Files */}
                      <div className="mt-6 space-y-4">
                        <div className="flex justify-between items-center">
                          <h4>Content</h4>
                          <Button
                            variant="outline"
                            className="flex outline-button"
                            size="sm"
                            onClick={() => openFileModal(module._id)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Files
                          </Button>
                        </div>

                        {module.files.length === 0 ? (
                          <div className="text-center py-8 border border-dashed border-mediumgray rounded-lg bg-lightergray">
                            <Paperclip className="h-8 w-8 mx-auto mb-2 text-darkgray" />
                            <p className="text-sm text-darkergray">
                              No files attached yet
                            </p>
                            <p className="text-xs text-darkgray mt-1">
                              Click "Add Files" to attach content
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {module.files.map((file, fileIndex) => (
                              <div
                                key={file._id}
                                className={`flex items-center justify-between p-3 rounded-lg ${
                                  draggedItem?.type === "file" &&
                                  draggedItem.fileId === file._id
                                    ? "border border-blue-500 opacity-50 bg-blue-50"
                                    : dragOverItem?.type === "file" &&
                                      dragOverItem.fileId === file._id
                                    ? "border border-blue-300 bg-blue-50"
                                    : "bg-lightergray border border-mediumgray"
                                } transition-all`}
                                draggable
                                onDragStart={() =>
                                  handleFileDragStart(module._id, file._id)
                                }
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  handleFileDragOver(module._id, file._id);
                                }}
                                onDragEnd={handleFileDrop}
                              >
                                <div className="flex items-center space-x-3">
                                  <div
                                    className="cursor-move p-1 text-darkgray hover:text-darkergray"
                                    onMouseDown={() =>
                                      handleFileDragStart(module._id, file._id)
                                    }
                                  >
                                    <GripVertical className="h-4 w-4" />
                                  </div>
                                  <div className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-mediumgray text-darkergray text-xs font-medium">
                                    {file.order}
                                  </div>
                                  <div
                                    className="flex items-center space-x-2 cursor-pointer hover:text-blue-600"
                                    onClick={() => openPreview(file)}
                                  >
                                    {getFileIcon(file.type)}
                                    <div>
                                      <p className="text-sm font-medium text-darkergray">
                                        {file.title}
                                      </p>
                                      <p className="text-xs text-darkgray">
                                        {formatFileSize(file.size)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openPreview(file)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Badge
                                    className={getFileTypeColor(file.type)}
                                  >
                                    {file.type.toUpperCase() || "FILE"}
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      removeFileFromModule(module._id, file._id)
                                    }
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Files Tab */}
        {activeTab === "files" && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <h3>File Management</h3>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1 bg-white border border-mediumgray rounded-md p-1">
                  <Button
                    className={`p-1 rounded ${
                      activeFileView === "grid" ? "bg-lightgray" : ""
                    }`}
                    onClick={() => setActiveFileView("grid")}
                  >
                    <LayoutGrid className="h-5 w-4" />
                  </Button>
                  <Button
                    className={`p-1 rounded ${
                      activeFileView === "list" ? "bg-lightgray" : ""
                    }`}
                    onClick={() => setActiveFileView("list")}
                  >
                    <List className="h-5 w-5" />
                  </Button>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-darkgray" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-mediumgray rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-mediumgray focus:border-darkgray  pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Upload Area */}

            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? "border-blue-500 bg-blue-50"
                  : "border-mediumgray hover:border-darkgray bg-white"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="mx-auto h-12 w-12 text-darkgray mb-4" />
              <div className="space-y-2">
                <p className="text-lg font-medium text-darkergray">
                  Drop files here or click to upload
                </p>
                <p className="text-sm text-darkgray">
                  Supports videos, PDFs, images, presentations and more
                </p>
              </div>
              {loading ? (
                <div className="flex items-center justify-center my-3 text-darkergray">
                  <i className="fa-solid fa-spinner animate-spin mr-2"></i>{" "}
                  Uploading ...
                </div>
              ) : (
                <>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-4 gray-button"
                  >
                    Choose Files
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileInput}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4,.mp3,.mov,.avi,.jpg,.jpeg,.png,.gif"
                  />
                </>
              )}
            </div>

            {/* Files List */}
            {uploadedFiles.length === 0 ? (
              <div className="bg-white p-12 rounded-lg shadow-sm border border-mediumgray text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-lightgray">
                  <FileText className="h-6 w-6 text-darkergray" />
                </div>
                <h3 className="mt-2">No files uploaded</h3>
                <p className="mt-1 text-sm text-darkergray">
                  Upload files to get started.
                </p>
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className="bg-white p-12 rounded-lg shadow-sm border border-mediumgray text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-lightgray">
                  <Search className="h-6 w-6 text-darkergray" />
                </div>
                <h3 className="mt-2">No matching files</h3>
                <p className="mt-1 text-sm text-darkergray">
                  Try adjusting your search term.
                </p>
                <Button
                  variant="outline"
                  className="mt-4 outline-button"
                  onClick={() => setSearchTerm("")}
                >
                  Clear Search
                </Button>
              </div>
            ) : activeFileView === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredFiles.map((file) => (
                  <div
                    key={file._id}
                    className="bg-white border border-mediumgray rounded-lg overflow-hidden hover:border-mediumgray transition-all"
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          {getFileIcon(file.type)}
                          <Badge className={getFileTypeColor(file.type)}>
                            {file.type.toUpperCase() || "FILE"}
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPreview(file)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowFileDetails(
                                file._id === showFileDetails ? null : file._id
                              );
                            }}
                          >
                            {file._id === showFileDetails ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(file._id);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {editingFile === file._id ? (
                        <div className="space-y-2">
                          <input
                            value={file.title}
                            onChange={(e) =>
                              updateFile(file._id, { title: e.target.value })
                            }
                            placeholder="Title"
                            className="w-full px-3 py-2 border border-mediumgray rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-mediumgray focus:border-darkgray text-sm"
                          />
                          <textarea
                            value={file.description}
                            onChange={(e) =>
                              updateFile(file._id, {
                                description: e.target.value,
                              })
                            }
                            placeholder="Description"
                            className="w-full px-3 py-2 border border-mediumgray rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-mediumgray focus:border-darkgray text-sm"
                            rows={2}
                          />
                          <div className="flex justify-end">
                            <Button
                              className="flex items-center black-button"
                              size="sm"
                              onClick={(e) => {
                                handleUpdateFile(
                                  file._id,
                                  file.title,
                                  file.description
                                );
                              }}
                            >
                              <Save className="h-3 w-3 mr-1" />
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <h3
                            className=" mb-1 truncate cursor-pointer hover:text-darkergray"
                            onClick={() => openPreview(file)}
                          >
                            {file.title}
                          </h3>
                          {file.description && (
                            <p className="text-sm text-darkergray line-clamp-2">
                              {file.description}
                            </p>
                          )}
                        </>
                      )}

                      {file._id === showFileDetails && (
                        <div className="mt-3 pt-3 border-t border-lightgray space-y-2">
                          <div className="flex justify-between text-xs text-darkergray">
                            <span>Size:</span>
                            <span>{formatFileSize(file.size)}</span>
                          </div>
                          <div className="flex justify-between text-xs text-darkergray">
                            <span>Uploaded:</span>
                            <span>{formatDateToAEDT(file.uploadDate)}</span>
                          </div>
                          <div className="flex justify-between text-xs text-darkergray">
                            <span>Type:</span>
                            <span>{file.type}</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center outline-button w-full mt-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingFile(file._id);
                            }}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit Details
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-mediumgray overflow-hidden">
                <table className="min-w-full divide-y divide-mediumgray">
                  <thead className="bg-lightergray">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-darkergray uppercase tracking-wider"
                      >
                        File
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-darkergray uppercase tracking-wider"
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-darkergray uppercase tracking-wider"
                      >
                        Size
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-darkergray uppercase tracking-wider"
                      >
                        Uploaded
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-mediumgray">
                    {filteredFiles.map((file) => (
                      <tr
                        key={file._id}
                        className="hover:bg-lightergray transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-lightgray">
                              {getFileIcon(file.type)}
                            </div>
                            <div className="ml-4">
                              <div
                                className="text-sm font-medium text-black cursor-pointer hover:text-darkergray"
                                onClick={() => openPreview(file)}
                              >
                                {file.title}
                              </div>
                              <div className="text-sm text-darkergray truncate max-w-xs">
                                {file.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={getFileTypeColor(file.type)}>
                            {file.type.toUpperCase() || "FILE"}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-darkergray">
                          {formatFileSize(file.size)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-darkergray">
                          {formatDateToAEDT(file.uploadDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openPreview(file)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {activeFileView === "grid" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingFile(file._id)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFile(file._id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Meetings Tab */}
        {activeTab === "meetings" && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <h3>Key Meeting Attachments</h3>
            </div>
            {meetingTypes.length === 0 && <h5>No Meetings Found</h5>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {meetingTypes.map((meeting) => (
                <div
                  key={meeting._id}
                  className="bg-white rounded-lg shadow-sm border border-mediumgray overflow-hidden"
                >
                  <div className="p-6">
                    <h3 className="mb-1">{meeting.title}</h3>
                    <p className="text-sm text-darkergray mb-4">
                      {meeting.description}
                    </p>

                    {/* Attached Files */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-darkergray flex items-center gap-2">
                          <Paperclip className="h-4 w-4" />
                          Attached Files
                        </h4>
                        <div className="flex items-center space-x-2">
                          <Badge variant="secondary">
                            {meeting.files.length}
                          </Badge>
                          <Button
                            variant="outline"
                            className="flex outline-button"
                            size="sm"
                            onClick={() => openFileModal(null, meeting._id)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Files
                          </Button>
                        </div>
                      </div>

                      {meeting.files.length === 0 ? (
                        <div className="text-center py-8 border border-dashed border-mediumgray rounded-lg bg-lightergray">
                          <Paperclip className="h-8 w-8 mx-auto mb-2 text-darkgray" />
                          <p className="text-sm text-darkergray">
                            No files attached yet
                          </p>
                          <p className="text-xs text-darkgray mt-1">
                            Click "Add Files" to attach content
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2 mt-3">
                          {meeting.files.map((file, index) => (
                            <div
                              key={file._id}
                              className="flex items-center justify-between p-3 bg-lightergray rounded-lg border border-mediumgray"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-mediumgray text-darkergray text-xs font-medium">
                                  {index + 1}
                                </div>
                                <div
                                  className="flex items-center space-x-2 cursor-pointer hover:text-blue-600"
                                  onClick={() => openPreview(file)}
                                >
                                  {getFileIcon(file.type)}
                                  <div>
                                    <p className="text-sm font-medium text-darkergray">
                                      {file.title}
                                    </p>
                                    <p className="text-xs text-darkgray">
                                      {formatFileSize(file.size)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openPreview(file)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Badge className={getFileTypeColor(file.type)}>
                                  {file.type.toUpperCase() || "FILE"}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    removeFileFromMeeting(meeting._id, file._id)
                                  }
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Price Process Tab */}
        {activeTab === "priceProcess" && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <h3>Price & Process Attachments</h3>
            </div>
            {priceProcess.length === 0 && (
              <h5>No Price and Process Attachments Found</h5>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {priceProcess.map((process) => (
                <div
                  key={process._id}
                  className="bg-white rounded-lg shadow-sm border border-mediumgray overflow-hidden"
                >
                  <div className="p-6">
                    <h3 className="mb-1">{process.title}</h3>
                    <p className="text-sm text-darkergray mb-4">
                      {process.description}
                    </p>

                    {/* Attached Files */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-darkergray flex items-center gap-2">
                          <Paperclip className="h-4 w-4" />
                          Attached Files
                        </h4>
                        <div className="flex items-center space-x-2">
                          <Badge variant="secondary">
                            {process.files.length}
                          </Badge>
                          <Button
                            variant="outline"
                            className="flex outline-button"
                            size="sm"
                            onClick={() =>
                              openFileModal(null, null, process._id)
                            }
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Files
                          </Button>
                        </div>
                      </div>

                      {process.files.length === 0 ? (
                        <div className="text-center py-8 border border-dashed border-mediumgray rounded-lg bg-lightergray">
                          <Paperclip className="h-8 w-8 mx-auto mb-2 text-darkgray" />
                          <p className="text-sm text-darkergray">
                            No files attached yet
                          </p>
                          <p className="text-xs text-darkgray mt-1">
                            Click "Add Files" to attach content
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2 mt-3">
                          {process.files.map((file, index) => (
                            <div
                              key={file._id}
                              className="flex items-center justify-between p-3 bg-lightergray rounded-lg border border-mediumgray"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-mediumgray text-darkergray text-xs font-medium">
                                  {index + 1}
                                </div>
                                <div
                                  className="flex items-center space-x-2 cursor-pointer hover:text-blue-600"
                                  onClick={() => openPreview(file)}
                                >
                                  {getFileIcon(file.type)}
                                  <div>
                                    <p className="text-sm font-medium text-darkergray">
                                      {file.title}
                                    </p>
                                    <p className="text-xs text-darkgray">
                                      {formatFileSize(file.size)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openPreview(file)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Badge className={getFileTypeColor(file.type)}>
                                  {file.type.toUpperCase() || "FILE"}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    removeFileFromProcess(process._id, file._id)
                                  }
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* File Selection Modal */}
      <Modal
        isOpen={showFileModal}
        onClose={closeFileModal}
        title="Select Files to Add"
      >
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-darkgray" />
            </div>
            <input
              type="text"
              placeholder="Search available files..."
              value={modalSearchTerm}
              onChange={(e) => setModalSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-mediumgray rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-mediumgray focus:border-darkgray  pl-10"
            />
          </div>

          {/* Selected Files Info */}
          {modalSelectedFiles.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="bg-blue-100 rounded-full p-1">
                  <Check className="h-4 w-4 text-blue-600" />
                </div>
                <p className="text-sm text-blue-800">
                  <span className="font-medium">{modalSelectedFiles.size}</span>{" "}
                  file(s) selected
                </p>
              </div>
              <Button
                className="outline-button"
                variant="outline"
                size="sm"
                onClick={() => setModalSelectedFiles(new Set())}
              >
                Clear
              </Button>
            </div>
          )}

          {/* Available Files */}
          <div className="max-h-96 overflow-y-auto">
            {filteredModalFiles.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-8 w-8 mx-auto mb-2 text-darkgray" />
                <p className="text-sm text-darkergray">
                  {getAvailableFiles().length === 0
                    ? "No available files to add"
                    : "No files match your search"}
                </p>
                {modalSearchTerm && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 outline-button"
                    onClick={() => setModalSearchTerm("")}
                  >
                    Clear Search
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {filteredModalFiles.map((file) => (
                  <div
                    key={file._id}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${
                      modalSelectedFiles.has(file._id)
                        ? "border-blue-500 bg-blue-50"
                        : "border-mediumgray hover:border-mediumgray hover:bg-lightergray"
                    }`}
                    onClick={() => toggleModalFileSelection(file._id)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        {getFileIcon(file.type)}
                        <div>
                          <p className="text-sm font-medium text-darkergray">
                            {file.title}
                          </p>
                          <p className="text-xs text-darkgray">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getFileTypeColor(file.type)}>
                        {file.type.toUpperCase() || "FILE"}
                      </Badge>
                      {modalSelectedFiles.has(file._id) && (
                        <div className="bg-blue-100 rounded-full p-1">
                          <Check className="h-3 w-3 text-blue-600" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Modal Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-mediumgray">
            <Button
              variant="outline"
              className="outline-button"
              onClick={closeFileModal}
            >
              Cancel
            </Button>
            <Button
              className="flex black-button"
              onClick={() => {
                selectedModuleId && addSelectedFilesToModule();
                selectedMeetingId && addSelectedFilesToMeeting();
                selectedProcessId && addSelectedFilesToProcess();
              }}
              disabled={modalSelectedFiles.size === 0}
            >
              Add {modalSelectedFiles.size > 0 ? modalSelectedFiles.size : ""}{" "}
              File
              {modalSelectedFiles.size !== 1 ? "s" : ""}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <PreviewModal
        isOpen={showPreviewModal}
        onClose={closePreview}
        file={previewFile}
      />
    </div>
  );
}
